// src/pages/Favorites.js 
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Container,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Favorite } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabaseClient';
import ProductCard from '../components/Product/ProductCard/ProductCard';

const Favorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchFavorites = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('user_favorites')
        .select(`
          id,
          product_id,
          created_at,
          products (
            id,
            name_ar,
            description_ar,
            price,
            new_price,
            category_id,
            categories (
              name
            ),
            product_colors (
              id,
              color_id,
              is_home_color,
              color_order,
              colors (
                name
              ),
              product_color_images (
                image_url,
                image_order
              ),
              product_color_sizes (
                size,
                stock
              )
            )
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to match ProductCard expectations
      const transformedFavorites = data.map(favorite => {
        const product = favorite.products;
        
        // Extract images prioritizing home color first, then other colors
        const images = [];
        if (product.product_colors && product.product_colors.length > 0) {
          // Find home color first
          const homeColor = product.product_colors.find(color => color.is_home_color);
          const otherColors = product.product_colors.filter(color => !color.is_home_color);
          
          // Sort other colors by color_order
          const sortedOtherColors = otherColors.sort((a, b) => {
            return (a.color_order || 0) - (b.color_order || 0);
          });

          // Process home color images first (if exists)
          if (homeColor && homeColor.product_color_images && homeColor.product_color_images.length > 0) {
            homeColor.product_color_images
              .sort((a, b) => (a.image_order || 0) - (b.image_order || 0))
              .forEach(img => {
                if (img.image_url) {
                  images.push(img.image_url);
                }
              });
          }

          // Then process other colors
          sortedOtherColors.forEach(color => {
            if (color.product_color_images && color.product_color_images.length > 0) {
              color.product_color_images
                .sort((a, b) => (a.image_order || 0) - (b.image_order || 0))
                .forEach(img => {
                  if (img.image_url) {
                    images.push(img.image_url);
                  }
                });
            }
          });

          // If no home color was found, fall back to first color by color_order
          if (images.length === 0) {
            const sortedColors = [...product.product_colors].sort((a, b) => {
              return (a.color_order || 0) - (b.color_order || 0);
            });

            if (sortedColors[0] && sortedColors[0].product_color_images && sortedColors[0].product_color_images.length > 0) {
              sortedColors[0].product_color_images
                .sort((a, b) => (a.image_order || 0) - (b.image_order || 0))
                .forEach(img => {
                  if (img.image_url) {
                    images.push(img.image_url);
                  }
                });
            }
          }
        }

        // Calculate total stock from all color sizes
        let totalStock = 0;
        
        if (product.product_colors && product.product_colors.length > 0) {
          product.product_colors.forEach(color => {
            if (color.product_color_sizes && color.product_color_sizes.length > 0) {
              color.product_color_sizes.forEach(size => {
                totalStock += size.stock || 0;
              });
            }
          });
        }

        return {
          id: product.id,
          name_ar: product.name_ar,
          description_ar: product.description_ar,
          price: product.price || 0,
          newPrice: product.new_price || null, // Use the new_price from products table
          new_price: product.new_price || null, // Keep both for compatibility
          stock: totalStock,
          images: images.length > 0 ? images : ['/placeholder-image.png'],
          product_colors: product.product_colors || [],
          category: product.categories?.name || 'غير مصنف',
          category_id: product.category_id
        };
      });

      setFavorites(transformedFavorites);
    } catch (err) {
      console.error('Error fetching favorites:', err);
      setError('حدث خطأ في تحميل المفضلة');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchFavorites();
    }
  }, [user, fetchFavorites]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchFavorites}>
          إعادة المحاولة
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          المفضلة
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {favorites.length} منتج في المفضلة
        </Typography>
      </Box>

      {favorites.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          textAlign="center"
        >
          <Favorite sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            لا توجد منتجات في المفضلة
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            ابدأ بإضافة منتجاتك المفضلة لتجدها هنا
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/department/children')}
            sx={{ px: 4 }}
          >
            تصفح المنتجات
          </Button>
        </Box>
      ) : (
        <Box sx={{ mb: 4 }}>
          {/* Mobile Layout - Match Home and Products page styling */}
          {isMobile ? (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)', // 2 cards per row
              gap: 1,
              px: 1,
              // Each card container will have consistent dimensions
              '& > *': {
                minWidth: '150px', // Match Home page dimensions
                maxWidth: '150px',
                justifySelf: 'center' // Center cards in their grid cells
              }
            }}>
              {favorites.map((product) => (
                <Box key={product.id}>
                  <ProductCard product={product} />
                </Box>
              ))}
            </Box>
          ) : (
            /* Desktop Layout - Use grid layout similar to Home page */
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
                xl: 'repeat(5, 1fr)'
              },
              gap: { sm: 2, md: 3 }
            }}>
              {favorites.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
};

export default Favorites;
